import { useRef, useState } from "react";
import {
  clientApiService,
  ProjectItem,
} from "../../services/client-api.service";

interface UploadDocumentModalProps {
  projects: ProjectItem[];
  onClose: () => void;
  onSuccess: () => void;
}

const CATEGORIES = [
  "Arquitectónico",
  "Estructural",
  "Eléctrico",
  "Hidrosanitario",
  "Legal",
  "Financiero",
  "Otro",
];

const MAX_SIZE_MB = 25;

export function UploadDocumentModal({
  projects,
  onClose,
  onSuccess,
}: UploadDocumentModalProps) {
  const [projectId, setProjectId] = useState("");
  const [name, setName] = useState("");
  const [category, setCategory] = useState("Otro");
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;

    if (f.size > MAX_SIZE_MB * 1024 * 1024) {
      setError(`El archivo excede ${MAX_SIZE_MB}MB`);
      return;
    }

    const allowed = [
      "image/png",
      "image/jpeg",
      "image/webp",
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ];
    if (!allowed.includes(f.type)) {
      setError("Formato no soportado (PDF, imágenes, Word, Excel)");
      return;
    }

    setFile(f);
    setName(f.name);
    setError(null);
  };

  const handleSubmit = async () => {
    if (!projectId || !file || !name.trim()) {
      setError("Proyecto, nombre y archivo son requeridos");
      return;
    }

    setIsUploading(true);
    setError(null);
    setProgress(10);

    try {
      const presigned = await clientApiService.presignDocumentUpload({
        filename: file.name,
        contentType: file.type,
        projectId,
      });
      setProgress(40);

      const uploadRes = await fetch(presigned.upload_url, {
        method: "PUT",
        headers: { "Content-Type": file.type },
        body: file,
      });
      if (!uploadRes.ok) {
        throw new Error(`Error al subir archivo (${uploadRes.status})`);
      }
      setProgress(75);

      await clientApiService.createDocument({
        projectId,
        name: name.trim(),
        category,
        url: presigned.public_url,
        size: file.size,
      });
      setProgress(100);
      onSuccess();
    } catch (err: any) {
      setError(err?.message || "Error al subir documento");
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-slate-900">Subir documento</h3>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700 text-xl"
          >
            ×
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">
              Proyecto <span className="text-primary">*</span>
            </label>
            <select
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
              className="w-full px-3 py-2.5 border border-slate-200 rounded-lg bg-slate-50 focus:bg-white focus:border-primary outline-none text-sm"
            >
              <option value="">Selecciona un proyecto...</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">
              Nombre del documento <span className="text-primary">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Nombre del documento"
              maxLength={150}
              className="w-full px-3 py-2.5 border border-slate-200 rounded-lg bg-slate-50 focus:bg-white focus:border-primary outline-none text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">
              Categoría
            </label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full px-3 py-2.5 border border-slate-200 rounded-lg bg-slate-50 focus:bg-white focus:border-primary outline-none text-sm"
            >
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">
              Archivo <span className="text-primary">*</span>
            </label>
            <input
              ref={fileRef}
              type="file"
              onChange={handleFile}
              accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.xls,.xlsx"
              className="block w-full text-sm text-slate-500 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-slate-100 file:text-slate-700 file:font-medium hover:file:bg-slate-200 cursor-pointer"
            />
            {file && (
              <p className="text-xs text-slate-400 mt-1">
                {file.name} ({(file.size / 1024).toFixed(0)} KB)
              </p>
            )}
            <p className="text-xs text-slate-400 mt-1">
              PDF, imágenes, Word o Excel. Máximo {MAX_SIZE_MB}MB.
            </p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3">
              {error}
            </div>
          )}

          {isUploading && progress > 0 && (
            <div>
              <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary transition-all"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <p className="text-xs text-slate-400 text-center mt-1">
                {progress}%
              </p>
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button
              onClick={onClose}
              disabled={isUploading}
              className="flex-1 py-2.5 border border-slate-200 rounded-lg text-slate-700 font-medium hover:bg-slate-50 transition disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              onClick={handleSubmit}
              disabled={isUploading || !file || !projectId || !name.trim()}
              className="flex-1 py-2.5 bg-primary hover:bg-primary-dark text-white rounded-lg font-semibold transition disabled:opacity-50"
            >
              {isUploading ? "Subiendo..." : "Subir documento"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}